import type {LocaleMessages} from './types';

export const ja: LocaleMessages = {
    common: {
        actions: {
            save: '保存',
            cancel: 'キャンセル',
            delete: '削除',
            edit: '編集',
            update: '更新',
            create: '作成',
            submit: '送信',
            reset: 'リセット',
            clear: 'クリア',
            search: '検索',
            filter: 'フィルター',
            sort: 'ソート',
            refresh: '更新',
            reload: '再読み込み',
            back: '戻る',
            next: '次へ',
            previous: '前へ',
            continue: '続行',
            finish: '完了',
            close: '閉じる',
            open: '開く',
            show: '表示',
            hide: '非表示',
            copy: 'コピー',
            paste: '貼り付け',
            cut: '切り取り',
            select: '選択',
            selectAll: 'すべて選択',
            deselect: '選択解除',
            confirm: '確認',
            deny: '拒否',
            approve: '承認',
            reject: '却下',
            retry: '再試行',
            undo: '元に戻す',
            redo: 'やり直し'
        },
        status: {
            loading: '読み込み中...',
            success: '成功',
            error: 'エラー',
            warning: '警告',
            info: '情報',
            pending: '保留中',
            completed: '完了',
            failed: '失敗',
            cancelled: 'キャンセル済み',
            active: 'アクティブ',
            inactive: '非アクティブ',
            enabled: '有効',
            disabled: '無効',
            online: 'オンライン',
            offline: 'オフライン',
            connected: '接続済み',
            disconnected: '未接続'
        },
        timeAgo: {
            justNow: 'たった今',
            minutesAgo: '{count}分前',
            hoursAgo: '{count}時間前',
            daysAgo: '{count}日前',
            weeksAgo: '{count}週間前',
            monthsAgo: '{count}ヶ月前',
            yearsAgo: '{count}年前'
        },
        navigation: {
            home: 'ホーム',
            dashboard: 'ダッシュボード',
            profile: 'プロフィール',
            settings: '設定',
            help: 'ヘルプ',
            support: 'サポート',
            documentation: 'ドキュメント',
            feedback: 'フィードバック',
            about: '概要',
            privacy: 'プライバシー',
            terms: '利用規約',
            contact: 'お問い合わせ'
        },
        general: {
            yes: 'はい',
            no: 'いいえ',
            ok: 'OK',
            maybe: 'たぶん',
            none: 'なし',
            all: 'すべて',
            other: 'その他',
            unknown: '不明',
            optional: 'オプション',
            required: '必須',
            recommended: '推奨',
            advanced: '高度',
            basic: '基本',
            custom: 'カスタム',
            default: 'デフォルト',
            example: '例',
            placeholder: 'プレースホルダー',
            noData: 'データなし',
            noResults: '結果なし',
            empty: '空',
            total: '合計',
            subtotal: '小計',
            count: '件数',
            limit: '制限',
            unlimited: '無制限'
        }
    },
    auth: {
        signIn: {
            title: 'サインイン',
            subtitle: 'アカウントにサインイン',
            emailLabel: 'メールアドレス',
            emailPlaceholder: 'メールアドレスを入力',
            usernameLabel: 'ユーザー名',
            usernamePlaceholder: 'ユーザー名を入力',
            passwordLabel: 'パスワード',
            passwordPlaceholder: 'パスワードを入力',
            rememberMe: 'ログイン状態を保持',
            forgotPassword: 'パスワードを忘れた場合',
            signInButton: 'サインイン',
            signInWithProvider: '{provider}でサインイン',
            orDivider: 'または',
            noAccount: 'アカウントをお持ちでない場合',
            createAccount: 'アカウント作成',
            success: 'サインインしました',
            welcomeBack: 'おかえりなさい！'
        },
        signUp: {
            title: 'サインアップ',
            subtitle: 'アカウントを作成',
            firstNameLabel: '名',
            firstNamePlaceholder: '名を入力',
            lastNameLabel: '姓',
            lastNamePlaceholder: '姓を入力',
            emailLabel: 'メールアドレス',
            emailPlaceholder: 'メールアドレスを入力',
            usernameLabel: 'ユーザー名',
            usernamePlaceholder: 'ユーザー名を選択',
            passwordLabel: 'パスワード',
            passwordPlaceholder: 'パスワードを作成',
            confirmPasswordLabel: 'パスワード確認',
            confirmPasswordPlaceholder: 'パスワードを再入力',
            phoneLabel: '電話番号',
            phonePlaceholder: '電話番号を入力',
            signUpButton: 'サインアップ',
            signUpWithProvider: '{provider}でサインアップ',
            orDivider: 'または',
            hasAccount: 'すでにアカウントをお持ちの場合',
            signInLink: 'サインイン',
            termsAcceptance: '{terms}と{privacy}に同意します',
            termsOfService: '利用規約',
            privacyPolicy: 'プライバシーポリシー',
            success: 'サインアップ完了',
            welcomeMessage: 'ようこそ！'
        },
        passwordReset: {
            title: 'パスワードリセット',
            subtitle: 'パスワードリセット用のリンクをお送りします',
            emailLabel: 'メールアドレス',
            emailPlaceholder: 'メールアドレスを入力',
            sendResetButton: 'リセットリンクを送信',
            backToSignIn: 'サインインに戻る',
            success: 'リンクを送信しました！',
            checkEmail: 'メールでリセットリンクをご確認ください',
            resetPassword: '新しいパスワードを設定',
            newPasswordLabel: '新しいパスワード',
            newPasswordPlaceholder: '新しいパスワードを入力',
            confirmNewPasswordLabel: '新しいパスワード確認',
            confirmNewPasswordPlaceholder: '新しいパスワードを再入力',
            resetButton: 'パスワードをリセット',
            passwordUpdated: 'パスワードを更新しました'
        },
        mfa: {
            title: '二要素認証',
            subtitle: '認証アプリからコードを入力してください',
            codeLabel: '認証コード',
            codePlaceholder: '6桁のコードを入力',
            verifyButton: '認証',
            resendCode: 'コードを再送信',
            useBackupCode: 'バックアップコードを使用',
            backupCodeLabel: 'バックアップコード',
            backupCodePlaceholder: 'バックアップコードを入力',
            useAuthenticator: '認証アプリを使用',
            success: '認証に成功しました',
            setup: {
                title: '二要素認証を設定',
                subtitle: 'アカウントにセキュリティ層を追加',
                step1: '認証アプリをダウンロード',
                step2: 'QRコードをスキャン',
                step3: '認証コードを入力',
                downloadApp: 'Google AuthenticatorまたはAuthyをダウンロード',
                scanQR: 'アプリでこのQRコードをスキャン',
                enterCode: 'アプリから6桁のコードを入力',
                manualEntry: '手動入力',
                secretKey: 'シークレットキー',
                verificationCode: '認証コード',
                enableButton: '2FAを有効にする',
                backupCodes: 'バックアップコード',
                saveBackupCodes: 'これらのコードを安全な場所に保存',
                backupCodesWarning: 'これらのコードは一度だけ使用できます'
            }
        },
        passkeys: {
            title: 'パスキー',
            subtitle: 'パスワードなしで安全にサインイン',
            setup: 'パスキーを設定',
            use: 'パスキーを使用',
            notSupported: 'このブラウザではパスキーがサポートされていません',
            setupButton: 'パスキーを設定',
            useButton: 'パスキーを使用',
            success: 'パスキーの設定が完了しました',
            registered: 'パスキーが登録されました',
            manage: {
                title: 'パスキーの管理',
                noPasskeys: 'パスキーが設定されていません',
                addPasskey: 'パスキーを追加',
                deviceName: 'デバイス名',
                createdAt: '作成日',
                lastUsed: '最終使用日',
                remove: '削除',
                removeConfirm: 'このパスキーを削除してもよろしいですか？'
            }
        },
        oauth: {
            signInWith: '{provider}でサインイン',
            signUpWith: '{provider}でサインアップ',
            continueWith: '{provider}で続行',
            connecting: '接続中...',
            success: '接続に成功しました',
            error: '接続エラー',
            cancelled: '接続がキャンセルされました',
            providers: {
                google: 'Google',
                github: 'GitHub',
                microsoft: 'Microsoft',
                facebook: 'Facebook',
                apple: 'Apple',
                twitter: 'Twitter',
                linkedin: 'LinkedIn',
                discord: 'Discord',
                slack: 'Slack',
                spotify: 'Spotify'
            }
        },
        verification: {
            email: {
                title: 'メール認証',
                subtitle: 'メールアドレスに認証リンクを送信しました',
                checkEmail: 'メールをご確認ください',
                resendEmail: 'メールを再送信',
                changeEmail: 'メールアドレスを変更',
                success: 'メールが認証されました',
                verified: 'メールアドレスの認証が完了しました'
            },
            phone: {
                title: '電話番号認証',
                subtitle: '電話番号に送信されたコードを入力してください',
                codeLabel: '認証コード',
                codePlaceholder: '6桁のコードを入力',
                resendCode: 'コードを再送信',
                verifyButton: '認証',
                success: '電話番号が認証されました',
                verified: '電話番号の認証が完了しました'
            }
        },
        magicLink: {
            title: 'マジックリンク',
            subtitle: 'サインイン用の安全なリンクをお送りします',
            emailLabel: 'メールアドレス',
            emailPlaceholder: 'メールアドレスを入力',
            sendLinkButton: 'マジックリンクを送信',
            checkEmail: 'メールでマジックリンクをご確認ください',
            success: 'マジックリンクを送信しました',
            expired: 'マジックリンクの有効期限が切れています',
            invalid: 'マジックリンクが無効です'
        },
        logout: {
            title: 'サインアウト',
            subtitle: 'サインアウトしてもよろしいですか？',
            button: 'サインアウト',
            confirm: 'はい、サインアウト',
            success: 'サインアウトしました',
            goodbye: 'またお会いしましょう！'
        },
        session: {
            expired: 'セッションの有効期限が切れました',
            invalid: '無効なセッションです',
            refresh: 'セッションを更新',
            refreshing: 'セッション更新中...',
            multipleWarning: '複数のセッションが検出されました',
            deviceLimit: 'デバイス制限に達しました'
        }
    },
    user: {
        profile: {
            title: 'プロフィール',
            personalInfo: '個人情報',
            accountInfo: 'アカウント情報',
            security: 'セキュリティ',
            preferences: '設定',
            firstName: '名',
            lastName: '姓',
            email: 'メールアドレス',
            username: 'ユーザー名',
            phone: '電話番号',
            bio: '自己紹介',
            website: 'ウェブサイト',
            location: '所在地',
            timezone: 'タイムゾーン',
            language: '言語',
            avatar: 'アバター',
            changeAvatar: 'アバターを変更',
            removeAvatar: 'アバターを削除',
            updateProfile: 'プロフィールを更新',
            profileUpdated: 'プロフィールを更新しました'
        },
        security: {
            title: 'セキュリティ',
            changePassword: 'パスワード変更',
            currentPassword: '現在のパスワード',
            newPassword: '新しいパスワード',
            confirmPassword: 'パスワード確認',
            passwordStrength: 'パスワード強度',
            passwordRequirements: 'パスワード要件',
            twoFactor: '二要素認証',
            enableTwoFactor: '2FAを有効にする',
            disableTwoFactor: '2FAを無効にする',
            twoFactorEnabled: '2FAが有効になりました',
            twoFactorDisabled: '2FAが無効になりました',
            backupCodes: 'バックアップコード',
            viewBackupCodes: 'バックアップコードを表示',
            regenerateBackupCodes: 'バックアップコードを再生成',
            devices: 'デバイス',
            trustedDevices: '信頼済みデバイス',
            removeDevice: 'デバイスを削除',
            deviceRemoved: 'デバイスを削除しました',
            sessions: 'セッション',
            activeSessions: 'アクティブセッション',
            terminateSession: 'セッションを終了',
            terminateAllSessions: 'すべてのセッションを終了',
            sessionTerminated: 'セッションを終了しました',
            connectedAccounts: '連携アカウント',
            disconnect: '連携解除',
            accountDisconnected: 'アカウントの連携を解除しました',
            deleteAccount: 'アカウント削除',
            deleteAccountWarning: 'この操作は元に戻せません',
            deleteAccountConfirm: 'アカウントを削除',
            accountDeleted: 'アカウントを削除しました'
        },
        preferences: {
            title: '設定',
            theme: 'テーマ',
            lightMode: 'ライトモード',
            darkMode: 'ダークモード',
            systemMode: 'システム',
            notifications: '通知',
            emailNotifications: 'メール通知',
            pushNotifications: 'プッシュ通知',
            smsNotifications: 'SMS通知',
            marketingEmails: 'マーケティングメール',
            securityAlerts: 'セキュリティアラート',
            accountActivity: 'アカウント活動',
            productUpdates: '製品アップデート',
            preferences: '設定',
            preferencesUpdated: '設定を更新しました'
        }
    },
    organization: {
        general: {
            title: '組織',
            name: '名前',
            description: '説明',
            website: 'ウェブサイト',
            logo: 'ロゴ',
            settings: '設定',
            members: 'メンバー',
            billing: '請求',
            usage: '使用状況',
            security: 'セキュリティ',
            integrations: '連携',
            audit: '監査',
            support: 'サポート'
        },
        profile: {
            title: '組織プロフィール',
            updateOrganization: '組織を更新',
            organizationUpdated: '組織を更新しました',
            logo: 'ロゴ',
            changeLogo: 'ロゴを変更',
            removeLogo: 'ロゴを削除',
            brandColors: 'ブランドカラー',
            primaryColor: 'プライマリカラー',
            secondaryColor: 'セカンダリカラー',
            customBranding: 'カスタムブランディング',
            domain: 'ドメイン',
            customDomain: 'カスタムドメイン',
            verifyDomain: 'ドメインを認証',
            domainVerified: 'ドメインが認証されました',
            domainPending: 'ドメイン認証待ち',
            contact: '連絡先',
            contactEmail: '連絡先メール',
            supportUrl: 'サポートURL',
            address: '住所',
            country: '国',
            timezone: 'タイムゾーン'
        },
        members: {
            title: 'メンバー',
            totalMembers: '総メンバー数',
            activeMembers: 'アクティブメンバー',
            pendingInvitations: '招待待ち',
            roles: '役割',
            permissions: '権限',
            inviteMember: 'メンバーを招待',
            inviteMembers: 'メンバーを招待',
            emailAddress: 'メールアドレス',
            role: '役割',
            customMessage: 'カスタムメッセージ',
            sendInvitation: '招待を送信',
            invitationSent: '招待を送信しました',
            resendInvitation: '招待を再送信',
            cancelInvitation: '招待をキャンセル',
            invitationCancelled: '招待をキャンセルしました',
            memberName: 'メンバー名',
            memberEmail: 'メンバーメール',
            memberRole: 'メンバー役割',
            memberStatus: 'メンバーステータス',
            memberJoined: '参加日',
            lastActive: '最終活動',
            changeRole: '役割を変更',
            removeMember: 'メンバーを削除',
            removeMemberConfirm: 'このメンバーを削除してもよろしいですか？',
            memberRemoved: 'メンバーを削除しました',
            transferOwnership: '所有権を移転',
            transferOwnershipConfirm: '所有権を移転してもよろしいですか？',
            ownershipTransferred: '所有権を移転しました',
            bulkActions: '一括操作',
            selectAll: 'すべて選択',
            selectedMembers: '選択されたメンバー',
            bulkRemove: '選択されたメンバーを削除',
            bulkChangeRole: '選択されたメンバーの役割を変更',
            export: 'エクスポート',
            exportMembers: 'メンバーをエクスポート'
        },
        roles: {
            title: '役割',
            createRole: '役割を作成',
            editRole: '役割を編集',
            deleteRole: '役割を削除',
            roleName: '役割名',
            roleDescription: '役割の説明',
            permissions: '権限',
            systemPermissions: 'システム権限',
            organizationPermissions: '組織権限',
            memberPermissions: 'メンバー権限',
            billingPermissions: '請求権限',
            defaultRole: 'デフォルト役割',
            customRole: 'カスタム役割',
            roleCreated: '役割を作成しました',
            roleUpdated: '役割を更新しました',
            roleDeleted: '役割を削除しました',
            assignRole: '役割を割り当て',
            unassignRole: '役割を解除',
            roleAssigned: '役割を割り当てました',
            roleUnassigned: '役割を解除しました',
            owner: 'オーナー',
            admin: '管理者',
            member: 'メンバー',
            guest: 'ゲスト',
            viewer: '閲覧者',
            editor: '編集者',
            manager: 'マネージャー'
        },
        settings: {
            title: '設定',
            general: '一般',
            authentication: '認証',
            security: 'セキュリティ',
            integrations: '連携',
            advanced: '高度',
            allowPublicSignup: '公開サインアップを許可',
            requireEmailVerification: 'メール認証を必須にする',
            allowUsernameSignup: 'ユーザー名でのサインアップを許可',
            passwordPolicy: 'パスワードポリシー',
            minPasswordLength: 'パスワードの最小長',
            requireUppercase: '大文字を必須にする',
            requireLowercase: '小文字を必須にする',
            requireNumbers: '数字を必須にする',
            requireSymbols: '記号を必須にする',
            sessionDuration: 'セッション時間',
            mfaRequired: 'MFAを必須にする',
            allowedDomains: '許可ドメイン',
            blockedDomains: 'ブロックドメイン',
            ipWhitelist: 'IPホワイトリスト',
            ssoEnabled: 'SSOを有効にする',
            ssoProvider: 'SSOプロバイダー',
            auditLogRetention: '監査ログ保持期間',
            dataExport: 'データエクスポート',
            deleteOrganization: '組織を削除',
            deleteOrganizationWarning: 'この操作により組織が完全に削除されます',
            deleteOrganizationConfirm: '組織を削除',
            organizationDeleted: '組織を削除しました',
            settingsUpdated: '設定を更新しました'
        },
        billing: {
            title: '請求',
            plan: 'プラン',
            currentPlan: '現在のプラン',
            usage: '使用状況',
            billing: '請求',
            invoices: '請求書',
            paymentMethod: '支払い方法',
            subscription: 'サブスクリプション',
            seats: 'シート',
            usedSeats: '使用中シート',
            availableSeats: '利用可能シート',
            addSeats: 'シートを追加',
            removeSeats: 'シートを削除',
            monthlyBilling: '月間請求',
            yearlyBilling: '年間請求',
            upgrade: 'アップグレード',
            downgrade: 'ダウングレード',
            cancel: 'キャンセル',
            pauseSubscription: 'サブスクリプションを一時停止',
            resumeSubscription: 'サブスクリプションを再開',
            billingHistory: '請求履歴',
            downloadInvoice: '請求書をダウンロード',
            updatePaymentMethod: '支払い方法を更新',
            paymentMethodUpdated: '支払い方法を更新しました',
            subscriptionUpdated: 'サブスクリプションを更新しました',
            subscriptionCancelled: 'サブスクリプションをキャンセルしました',
            subscriptionPaused: 'サブスクリプションを一時停止しました',
            subscriptionResumed: 'サブスクリプションを再開しました',
            trialExpired: 'トライアル期間が終了しました',
            trialDaysLeft: 'トライアル残り日数',
            upgradeNow: '今すぐアップグレード'
        },
        invitations: {
            title: '招待',
            youreInvited: '招待されました',
            invitedBy: '招待者',
            invitedTo: '招待先',
            roleOffered: '提供される役割',
            acceptInvitation: '招待を受け入れる',
            declineInvitation: '招待を辞退する',
            invitationAccepted: '招待を受け入れました',
            invitationDeclined: '招待を辞退しました',
            invitationExpired: '招待の有効期限が切れています',
            invitationInvalid: '招待が無効です',
            alreadyMember: 'すでにメンバーです',
            pendingInvitations: '招待待ち',
            sentInvitations: '送信済み招待',
            receivedInvitations: '受信済み招待',
            invitationDetails: '招待詳細',
            expiresAt: '有効期限',
            customMessage: 'カスタムメッセージ'
        }
    },
    validation: {
        required: 'この項目は必須です',
        email: '有効なメールアドレスを入力してください',
        phone: '有効な電話番号を入力してください',
        url: '有効なURLを入力してください',
        minLength: '最低{min}文字必要です',
        maxLength: '{max}文字以下で入力してください',
        min: '最小値は{min}です',
        max: '最大値は{max}です',
        pattern: '無効な形式です',
        passwordMismatch: 'パスワードが一致しません',
        invalidFormat: '無効な形式です',
        invalid: '無効です',
        tooShort: '短すぎます',
        tooLong: '長すぎます',
        tooSmall: '小さすぎます',
        tooLarge: '大きすぎます',
        notFound: '見つかりません',
        alreadyExists: 'すでに存在します',
        expired: '期限切れです',
        weak: '弱い',
        strong: '強い',
        medium: '中程度',
        passwordStrength: {
            veryWeak: '非常に弱い',
            weak: '弱い',
            fair: '普通',
            good: '良い',
            strong: '強い'
        },
        passwordRequirements: {
            minLength: '最低{min}文字',
            uppercase: '大文字1文字以上',
            lowercase: '小文字1文字以上',
            number: '数字1文字以上',
            symbol: '記号1文字以上'
        }
    },
    errors: {
        generic: {
            unknown: '不明なエラーが発生しました',
            network: 'ネットワークエラー',
            timeout: 'リクエストタイムアウト',
            serverError: 'サーバーエラー',
            notFound: '見つかりません',
            unauthorized: '認証されていません',
            forbidden: 'アクセスが拒否されました',
            badRequest: '不正なリクエスト',
            conflict: '競合',
            tooManyRequests: 'リクエストが多すぎます',
            maintenance: 'システムメンテナンス中',
            offline: 'インターネット接続がありません'
        },
        auth: {
            invalidCredentials: '認証情報が無効です',
            accountLocked: 'アカウントがロックされています',
            accountDisabled: 'アカウントが無効です',
            accountNotVerified: 'アカウントが認証されていません',
            sessionExpired: 'セッションが期限切れです',
            invalidToken: '無効なトークンです',
            mfaRequired: '二要素認証が必要です',
            invalidMfaCode: '無効なMFAコードです',
            passkeyNotSupported: 'パスキーがサポートされていません',
            passkeyFailed: 'パスキー認証に失敗しました',
            oauthFailed: 'OAuth認証に失敗しました',
            oauthCancelled: 'OAuth認証がキャンセルされました',
            magicLinkExpired: 'マジックリンクの有効期限が切れています',
            magicLinkInvalid: 'マジックリンクが無効です',
            passwordTooWeak: 'パスワードが弱すぎます',
            passwordReused: 'パスワードが再利用されています',
            emailNotVerified: 'メールが認証されていません',
            phoneNotVerified: '電話番号が認証されていません',
            invitationExpired: '招待の有効期限が切れています',
            invitationInvalid: '招待が無効です',
            invitationAlreadyAccepted: '招待はすでに受け入れられています'
        },
        organization: {
            notFound: '組織が見つかりません',
            accessDenied: '組織へのアクセスが拒否されました',
            memberLimitReached: 'メンバー制限に達しました',
            ownerRequired: 'オーナーが必要です',
            cannotRemoveOwner: 'オーナーを削除できません',
            cannotRemoveSelf: '自分自身を削除できません',
            alreadyMember: 'すでにメンバーです',
            notMember: 'メンバーではありません',
            invalidRole: '無効な役割です',
            roleNotFound: '役割が見つかりません',
            domainTaken: 'ドメインは既に使用されています',
            domainInvalid: '無効なドメインです',
            seatLimitReached: 'シート制限に達しました',
            subscriptionRequired: 'サブスクリプションが必要です',
            paymentFailed: '支払いに失敗しました'
        },
        validation: {
            invalidEmail: '無効なメールアドレスです',
            invalidPhone: '無効な電話番号です',
            invalidUrl: '無効なURLです',
            fieldRequired: 'この項目は必須です',
            fieldTooShort: '項目が短すぎます',
            fieldTooLong: '項目が長すぎます',
            fieldInvalid: '無効な項目です',
            passwordsDoNotMatch: 'パスワードが一致しません',
            emailTaken: 'メールアドレスは既に使用されています',
            usernameTaken: 'ユーザー名は既に使用されています',
            phoneNumberTaken: '電話番号は既に使用されています',
            domainTaken: 'ドメインは既に使用されています'
        }
    },
    success: {
        generic: {
            saved: '保存しました',
            updated: '更新しました',
            created: '作成しました',
            deleted: '削除しました',
            sent: '送信しました',
            completed: '完了しました',
            copied: 'クリップボードにコピーしました',
            uploaded: 'アップロードしました',
            downloaded: 'ダウンロードしました',
            imported: 'インポートしました',
            exported: 'エクスポートしました'
        },
        auth: {
            signedIn: 'サインインしました',
            signedUp: 'サインアップしました',
            signedOut: 'サインアウトしました',
            passwordChanged: 'パスワードを変更しました',
            passwordReset: 'パスワードをリセットしました',
            emailVerified: 'メールを認証しました',
            phoneVerified: '電話番号を認証しました',
            mfaEnabled: '二要素認証を有効にしました',
            mfaDisabled: '二要素認証を無効にしました',
            passkeyAdded: 'パスキーを追加しました',
            passkeyRemoved: 'パスキーを削除しました',
            accountConnected: 'アカウントを連携しました',
            accountDisconnected: 'アカウントの連携を解除しました'
        },
        organization: {
            created: '組織を作成しました',
            updated: '組織を更新しました',
            deleted: '組織を削除しました',
            memberInvited: 'メンバーを招待しました',
            memberRemoved: 'メンバーを削除しました',
            roleChanged: '役割を変更しました',
            ownershipTransferred: '所有権を移転しました',
            settingsUpdated: '設定を更新しました',
            domainVerified: 'ドメインを認証しました',
            subscriptionUpdated: 'サブスクリプションを更新しました',
            paymentMethodUpdated: '支払い方法を更新しました'
        }
    },
    components: {
        loading: {
            generic: '読み込み中...',
            signIn: 'サインイン中...',
            signUp: 'サインアップ中...',
            profile: 'プロフィール読み込み中...',
            organization: '組織読み込み中...',
            members: 'メンバー読み込み中...',
            settings: '設定読み込み中...',
            verification: '認証中...',
            mfa: 'MFA認証中...',
            passkey: 'パスキー設定中...',
            oauth: 'OAuth接続中...'
        },
        empty: {
            generic: 'データがありません',
            members: 'メンバーが見つかりません',
            invitations: '招待がありません',
            sessions: 'アクティブなセッションがありません',
            devices: '登録されたデバイスがありません',
            notifications: '通知がありません',
            history: '履歴がありません',
            logs: 'ログがありません',
            search: '検索結果が見つかりません',
            filter: 'フィルターに一致するアイテムがありません'
        },
        confirm: {
            delete: 'このアイテムを削除してもよろしいですか？',
            remove: 'このアイテムを削除してもよろしいですか？',
            cancel: 'キャンセルしてもよろしいですか？',
            logout: 'サインアウトしてもよろしいですか？',
            transfer: '移転してもよろしいですか？',
            disable: '無効にしてもよろしいですか？',
            enable: '有効にしてもよろしいですか？',
            reset: 'リセットしてもよろしいですか？',
            clear: 'クリアしてもよろしいですか？',
            proceed: '続行しますか？',
            areYouSure: '本当によろしいですか？',
            cannotUndo: 'この操作は元に戻せません',
            permanentAction: 'これは永続的な操作です'
        },
        help: {
            password: '文字、数字、記号を含む8文字以上を使用してください',
            mfa: '二要素認証により追加のセキュリティが提供されます',
            passkey: 'パスキーはパスワードよりも安全です',
            backup: 'バックアップコードを安全な場所に保存してください',
            domain: 'ドメインは使用前に認証が必要です',
            webhook: 'Webhookはリアルタイム通知を送信します',
            apiKey: 'APIキーはプログラマティックアクセスに使用します',
            role: '役割はユーザーの権限を決定します',
            permission: '権限は機能へのアクセスを制御します',
            billing: '請求は毎月処理されます',
            usage: '使用状況は現在のプランで監視されます'
        }
    }
};